import { Slider as BaseSlider } from "@base-ui/react/slider";

import { cn } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";

type SliderValue = number | readonly [number, number];

export interface SliderProps<V extends SliderValue> {
  /** What is set: "Price", "Max cut". Shown above the track unless hidden. */
  label: string;
  hideLabel?: boolean;
  /** One number, or a [from, to] pair for a range with two thumbs. */
  value?: V;
  defaultValue?: V;
  /** Every change while dragging. */
  onValueChange?: (value: V) => void;
  /** Once, when the drag ends or a key is released: the place to save or fetch. */
  onValueCommit?: (value: V) => void;
  min?: number;
  max?: number;
  step?: number;
  /** The words for one value, shown and read by screen readers: "$25", "3%". */
  format?: (value: number) => string;
  /** A range's thumbs' names: "Minimum price" and "Maximum price". */
  thumbLabels?: readonly [string, string];
  /** Shows the value (or "from – to") at the end of the label's line. */
  showValue?: boolean;
  disabled?: boolean;
  name?: string;
  className?: string;
}

/**
 * A number, or a range, set by dragging or with the arrow keys (Page Up and Down take bigger
 * steps). Every value it shows is also read out, through `format`.
 */
export function Slider<V extends SliderValue>({
  label,
  hideLabel = false,
  value,
  defaultValue,
  onValueChange,
  onValueCommit,
  min = 0,
  max = 100,
  step = 1,
  format = String,
  thumbLabels,
  showValue = true,
  disabled,
  name,
  className,
}: SliderProps<V>) {
  const shown = value ?? defaultValue ?? min;
  const isRange = Array.isArray(shown);
  const thumbs = isRange ? [0, 1] : [0];
  return (
    <BaseSlider.Root
      value={value as number | number[] | undefined}
      defaultValue={defaultValue as number | number[] | undefined}
      onValueChange={(next) => onValueChange?.(next as unknown as V)}
      onValueCommitted={(next) => onValueCommit?.(next as unknown as V)}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      name={name}
      className={cn("flex flex-col gap-2.5", className)}
    >
      <div className={cn("flex items-baseline justify-between gap-3", hideLabel && "sr-only")}>
        <BaseSlider.Label className="font-body text-body-b3-strong text-text-secondary">
          {label}
        </BaseSlider.Label>
        {showValue && (
          <BaseSlider.Value
            aria-hidden
            className="font-mono text-mono-meta text-text-primary tabular-nums"
          >
            {(_formatted, values) => values.map(format).join(" – ")}
          </BaseSlider.Value>
        )}
      </div>
      <BaseSlider.Control className="flex h-6 w-full touch-none items-center select-none data-disabled:opacity-40">
        <BaseSlider.Track className="relative h-1.5 w-full rounded-pill bg-bg-inset">
          <BaseSlider.Indicator className="rounded-pill bg-accent-default" />
          {thumbs.map((index) => (
            <BaseSlider.Thumb
              key={index}
              index={index}
              aria-label={isRange ? thumbLabels?.[index] : undefined}
              getAriaValueText={(_formatted, raw) => format(raw)}
              className={cn(
                "size-5 rounded-pill border-2 border-accent-default bg-text-on-accent shadow-float",
                "not-data-disabled:hover:scale-110 data-dragging:scale-110 transition-transform duration-fast ease-standard motion-reduce:transition-none",
                FOCUS_RING,
              )}
            />
          ))}
        </BaseSlider.Track>
      </BaseSlider.Control>
    </BaseSlider.Root>
  );
}
